<script setup lang="ts">
import { computed, type PropType } from 'vue';
import GaiaText from './GaiaText.vue';

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  /** default = the small/600 modal-form label; uppercase = the login idiom. */
  variant: {
    type: String as PropType<'default' | 'uppercase'>,
    default: 'default',
  },
  /** Red asterisk after the label. */
  required: {
    type: Boolean,
    default: false,
  },
  /** Faint "Optional" (or custom) hint beside the label. */
  hint: {
    type: String,
    default: undefined,
  },
  /** Inline error under the control, e.g. "Please enter a name". */
  error: {
    type: String,
    default: undefined,
  },
});

const isUpper = computed(() => props.variant === 'uppercase');
</script>

<template>
  <!-- Labelled form field from the CM modal forms and the Login mock. The two
       label idioms (small/600 vs uppercase micro) have no n-form-item theme
       vars, so the field row is self-contained; the control is the slot. -->
  <n-flex vertical :size="7" class="gaia-form-field">
    <n-flex align="baseline" :size="8">
      <gaia-text
        :variant="isUpper ? 'label' : 'default'"
        :size="isUpper ? 'micro' : 'control'"
        :tone="isUpper ? 'faint' : 'text'"
        :weight="isUpper ? undefined : 'semibold'"
      >
        {{ props.label }}<gaia-text v-if="props.required" tone="alert"> *</gaia-text>
      </gaia-text>
      <gaia-text v-if="props.hint" size="micro" tone="muted">{{ props.hint }}</gaia-text>
      <!-- Pinned right on the label's own line: "Forgot password?" belongs to
           the field, not under it, and a row below the input would read as a
           hint about what to type. -->
      <span v-if="$slots.action" :style="{ marginLeft: 'auto', flex: 'none' }">
        <slot name="action" />
      </span>
    </n-flex>
    <slot />
    <gaia-text v-if="props.error" size="meta" tone="alert">{{ props.error }}</gaia-text>
  </n-flex>
</template>
